<template>
  <div class="hot-list-controls">
    <!-- Hot List Selector Dropdown -->
    <div class="hot-list-selector">
      <button @click="toggleDropdown" class="selector-btn">
        <i class="fas fa-fire"></i>
        <span v-if="activeHotList">{{ activeHotList.name }}</span>
        <span v-else>Hot Lists</span>
        <div class="texto-lefty"> 
        <span v-if="hotLists.length > 0" class="badge">{{ hotLists.length }}</span>
        <i class="fas fa-chevron-down" :class="{ rotated: dropdownOpen }"></i>
      </div>
      </button>

      <!-- Dropdown Menu -->
      <transition name="dropdown-fade">
        <div v-if="dropdownOpen" class="dropdown-menu">
          <div class="dropdown-header">
            <span>{{ readOnly ? 'Available Hot Lists' : 'Your Hot Lists' }}</span>
            <!-- ✅ Only show create button for agents -->
            <button 
              v-if="!readOnly"
              @click="showCreateModal = true; dropdownOpen = false" 
              class="create-btn"
            >
              <i class="fas fa-plus"></i> Create New
            </button>
          </div>

          <div v-if="hotLists.length === 0" class="empty-state">
            <i class="fas fa-fire-alt"></i>
            <p>No hot lists yet</p>
            <small>Create one to start auto-matching candidates</small>
          </div>

          <div v-else class="hot-list-items">
            <div
              v-for="list in hotLists"
              :key="list.hot_list_id"
              @click="selectHotList(list)"
              class="hot-list-item"
              :class="{ active: activeHotList?.hot_list_id === list.hot_list_id }"
            >
              <div class="list-info">
                <div class="list-name">{{ list.name }}</div>
                <div class="list-meta">
                  <span class="candidate-count">
                    <i class="fas fa-users"></i>
                    {{ list.candidate_count }}
                  </span>
                  <span v-if="list.last_matched_at" class="last-matched">
                    Updated {{ formatTimeAgo(list.last_matched_at) }}
                  </span>
                  
                  
                 

                </div>

              </div>
              <div class="list-actions">
                <button @click.stop="toggleDetails(list)" class="icon-btn" title="Details">
                  <i class="fas fa-info-circle"></i>
                </button>
                <!-- <button @click.stop="refreshHotList(list.hot_list_id)" class="icon-btn" title="Refresh">
                  <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing === list.hot_list_id }"></i>
                </button> -->
              </div>
             <!-- ✅ Only show refresh for agents -->
            <button
            v-if="!readOnly"
            @click.stop="refreshHotList(list.hot_list_id)"
            class="refresh-btn"
            :disabled="refreshing === list.hot_list_id"
            title="Refresh matches"
            >
            <i class="fas fa-sync-alt" :class="{ spinning: refreshing === list.hot_list_id }"></i>
            </button>
            </div>

            <!-- Clear Selection -->
            <div v-if="activeHotList" @click="clearHotList" class="clear-selection">
              <i class="fas fa-times-circle"></i>
              Exit Hot List Mode
            </div>
          </div>
        </div>
      </transition>

      <!-- Expandable Details (outside dropdown) -->
<div v-if="expandedHotList" class="hot-list-details">
  <div class="details-header">
    <h3>{{ expandedHotList.name }}</h3>
    <div class="details-actions">
      <!-- ✅ Only show edit/delete for agents -->
      <template v-if="!readOnly">
        <button @click="openEditModal(expandedHotList)" class="edit-btn">
          <i class="fas fa-edit"></i> Edit
        </button>
        <button @click="deleteHotList(expandedHotList.hot_list_id, expandedHotList.name)" class="delete-btn">
          <i class="fas fa-trash"></i> Delete
        </button>
      </template>
      <button @click="expandedHotList = null" class="close-btn">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
  
  <div class="details-grid">
    <!-- Criteria Card -->
    <div class="detail-card">
      <div class="card-header">
        <i class="fas fa-filter"></i>
        <span>Criteria</span>
      </div>
      <div class="card-content">
        <div v-if="expandedHotList.criteria.job_title" class="detail-item">
          <label>Job Title:</label>
          <span>{{ expandedHotList.criteria.job_title }}</span>
        </div>
        <div v-if="expandedHotList.criteria.seniority" class="detail-item">
          <label>Seniority:</label>
          <span class="badge">{{ expandedHotList.criteria.seniority }}</span>
        </div>
        <div v-if="expandedHotList.criteria.location" class="detail-item">
          <label>Location:</label>
          <span>{{ expandedHotList.criteria.location }}</span>
        </div>
      </div>
    </div>
    
    <!-- Skills Card -->
    <div class="detail-card">
      <div class="card-header">
        <i class="fas fa-code"></i>
        <span>Skills</span>
      </div>
      <div class="card-content">
        <div v-if="expandedHotList.criteria.skills?.length" class="detail-item">
          <label>Required:</label>
          <div class="skills-tags">
            <span v-for="skill in expandedHotList.criteria.skills" :key="skill" class="skill-tag required">
              {{ skill }}
            </span>
          </div>
        </div>
        <div v-if="expandedHotList.criteria.nice_to_have_skills?.length" class="detail-item">
          <label>Nice-to-have:</label>
          <div class="skills-tags">
            <span v-for="skill in expandedHotList.criteria.nice_to_have_skills" :key="skill" class="skill-tag nice">
              {{ skill }}
            </span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Experience Card -->
    <div class="detail-card">
      <div class="card-header">
        <i class="fas fa-briefcase"></i>
        <span>Experience</span>
      </div>
      <div class="card-content">
        <div v-if="expandedHotList.criteria.min_experience_months" class="detail-item">
          <label>Minimum:</label>
          <span>{{ Math.floor(expandedHotList.criteria.min_experience_months / 12) }} years</span>
        </div>
        <div v-if="expandedHotList.criteria.max_experience_months" class="detail-item">
          <label>Maximum:</label>
          <span>{{ Math.floor(expandedHotList.criteria.max_experience_months / 12) }} years</span>
        </div>
      </div>
    </div>
    
    <!-- Settings Card -->
    <div class="detail-card">
      <div class="card-header">
        <i class="fas fa-cog"></i>
        <span>Settings</span>
      </div>
      <div class="card-content">
        <div class="detail-item">
          <label>Match Threshold:</label>
          <span>{{ Math.round(expandedHotList.match_threshold * 100) }}%</span>
        </div>
        <div class="detail-item">
          <label>Auto-refresh:</label>
          <span>{{ expandedHotList.auto_refresh ? 'Enabled' : 'Disabled' }}</span>
        </div>
        <div v-if="expandedHotList.last_matched_at" class="detail-item">
          <label>Last matched:</label>
          <span>{{ formatTimeAgo(expandedHotList.last_matched_at) }}</span>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Job Description -->
  <div v-if="expandedHotList.criteria.job_description" class="description-card">
    <div class="card-header">
      <i class="fas fa-file-alt"></i>
      <span>Job Description</span>
    </div>
    <div class="card-content">
      <p>{{ expandedHotList.criteria.job_description }}</p>
    </div>
  </div>
  
  <!-- Additional Context -->
  <div v-if="expandedHotList.criteria.additional_context" class="description-card">
    <div class="card-header">
      <i class="fas fa-info-circle"></i>
      <span>Additional Context</span>
    </div>
    <div class="card-content">
      <p>{{ expandedHotList.criteria.additional_context }}</p>
    </div>
  </div>
</div>



    </div>

    <!-- Active Hot List Banner (shows when hot list is selected) -->
    <transition name="slide-down">
      <div v-if="activeHotList" class="hot-list-banner">
        <div class="banner-content">
          <i class="fas fa-filter"></i>
          <span class="banner-text">
            Viewing: <strong>{{ activeHotList.name }}</strong>
            ({{ activeCandidateIds.length }} candidates)
          </span>
          <button @click="viewFullList" class="view-all-btn">
            <i class="fas fa-fire"></i>
            View All
          </button>
          <button @click="clearHotList" class="close-banner">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </transition>

    <!-- Create Hot List Modal -->
    <CreateHotListModal
      v-if="showCreateModal"
      :initial-data="editingHotList"
      @close="showCreateModal = false; editingHotList = null" 
      @created="handleHotListCreated"
    />
  </div>
</template>

<script> 
import axios from 'axios';
import CreateHotListModal from './CreateHotListModal.vue';

export default {
  name: 'HotListControls',
  props: {
  readOnly: {
    type: Boolean,
    default: false
  }
},
  components: {
    CreateHotListModal
  },
  
  data() {
    return {
      hotLists: [],
      expandedHotList: null,
editingHotList: null,
      activeHotList: null,
      activeCandidateIds: [],
      dropdownOpen: false,
      showCreateModal: false,
      refreshing: null,
      loading: false
    };
  },
  
  async mounted() {
  await this.loadHotLists();
  
  // Auto-activate hot list if coming from notification
  const urlParams = new URLSearchParams(window.location.search);
  const hotListId = urlParams.get('hot_list_id');
  const autoActivate = urlParams.get('auto_activate') === 'true';
  
  if (hotListId && autoActivate) {
    // Find the hot list
    const list = this.hotLists.find(l => l.hot_list_id === hotListId);
    
    if (list) {
      await this.selectHotList(list);
      console.log(`✅ Auto-activated hot list "${list.name}" from notification`);
      
      // Clean up URL params (optional - removes query params after activation)
      this.$router.replace({ 
        path: '/',
        query: {} 
      });
    } else {
      console.error(`❌ Hot list ${hotListId} not found in user's hot lists`);
    }
  }
  
  // Close dropdown when clicking outside
  document.addEventListener('click', this.handleClickOutside);
},
  
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  
  methods: {
    toggleDetails(hotList) {
  this.expandedHotList = this.expandedHotList?.hot_list_id === hotList.hot_list_id 
    ? null 
    : hotList;
},

openEditModal(hotList) {
  this.editingHotList = hotList;
  this.showCreateModal = true;
},

async deleteHotList(hotListId, hotListName) {
  if (!confirm(`Delete hot list "${hotListName}"? This will remove all ${this.getHotListCount(hotListId)} candidates.`)) {
    return;
  }
  
  try {
    await axios.delete(`/api/v1/hot-lists/${hotListId}`);
    
    // Clear if it was active
    if (this.activeHotList?.hot_list_id === hotListId) {
      this.clearHotList();
    }
    
    this.loadHotLists();
    console.log(`🗑️ Deleted hot list ${hotListName}`);
  } catch (error) {
    console.error('❌ Error deleting hot list:', error);
    alert('Failed to delete hot list');
  }
},

getHotListCount(hotListId) {
  const list = this.hotLists.find(l => l.hot_list_id === hotListId);
  return list?.candidate_count || 0;
},
    // async loadHotLists() {
    //   try {
    //     this.loading = true;
    //     const response = await axios.get('/api/v1/hot-lists');
    //     this.hotLists = response.data.hot_lists || [];
    //     console.log('✅ Loaded hot lists:', this.hotLists.length);
    //   } catch (error) {
    //     console.error('❌ Error loading hot lists:', error);
    //   } finally {
    //     this.loading = false;
    //   }
    // },
async loadHotLists() {
  try {
    this.loading = true;
    
    // ✅ Use different endpoint for clients (read-only)
    const endpoint = this.readOnly ? '/api/v1/hot-lists/shared' : '/api/v1/hot-lists';
    const response = await axios.get(endpoint);
    
    this.hotLists = response.data.hot_lists || [];
    console.log(`✅ Loaded ${this.hotLists.length} hot lists (read-only: ${this.readOnly})`);
    
  } catch (error) {
    console.error('Error loading hot lists:', error);
    this.hotLists = [];
  } finally {
    this.loading = false;
  }
},
    async selectHotList(list) {
      try {
        this.dropdownOpen = false;
        
        // Get candidate IDs for this hot list
        const response = await axios.get(`/api/v1/hot-lists/${list.hot_list_id}/candidate-ids`);
        
        this.activeHotList = list;
        this.activeCandidateIds = response.data.candidate_ids || [];
        
        console.log(`🔥 Activated hot list: ${list.name} (${this.activeCandidateIds.length} candidates)`);
        
        // Emit event to parent (HomeView) to filter searches
        this.$emit('hot-list-activated', {
          hot_list_id: list.hot_list_id,
          candidate_ids: this.activeCandidateIds,
          name: list.name
        });
        
      } catch (error) {
        console.error('❌ Error activating hot list:', error);
      }
    },
    
    clearHotList() {
      this.activeHotList = null;
      this.activeCandidateIds = [];
      this.dropdownOpen = false;
      
      // Emit event to clear filter
      this.$emit('hot-list-cleared');
      
      console.log('🚫 Hot list mode cleared');
    },
    
    // viewFullList() {
    //   if (!this.activeHotList || this.activeCandidateIds.length === 0) return;
      
    //   // Navigate using search_set pattern (like analytics)
    //   if (this.activeCandidateIds.length > 40) {
    //     this.createSearchSetAndNavigate();
    //   } else {
    //     this.$router.push({
    //       path: '/',
    //       query: {
    //         candidate_id: this.activeCandidateIds.join(','),
    //         auto_search: 'true',
    //         from: 'hot-list',
    //         hot_list_name: this.activeHotList.name
    //       }
    //     });
    //   }
    // },
//     async viewFullList() {
//   try {
//     // Fetch candidates WITH scores
//     const response = await axios.get(
//       `/api/v1/hot-lists/${this.activeHotList.hot_list_id}/candidates-with-scores`
//     );
    
//     const candidatesWithScores = response.data.candidates;
    
//     if (candidatesWithScores.length > 40) {
//       // Create search_set with metadata
//       const searchSetResponse = await axios.post('/api/v1/search-sets/', {
//         candidate_ids: candidatesWithScores.map(c => c.id),
//         metadata: {
//           hot_list_id: this.activeHotList.hot_list_id,
//           hot_list_name: this.activeHotList.name,
//           scores: candidatesWithScores.reduce((acc, c) => {
//             acc[c.id] = {
//               score: c.match_score,
//               metadata: c.metadata
//             };
//             return acc;
//           }, {})
//         }
//       });
      
//       this.$router.push({
//         path: '/',
//         query: {
//           search_set: searchSetResponse.data.search_set_id,
//           auto_search: 'true',
//           from: 'hot-list'
//         }
//       });
//     } else {
//       // Pass scores via query metadata (for small lists)
//       this.$router.push({
//         path: '/',
//         query: {
//           candidate_id: candidatesWithScores.map(c => c.id).join(','),
//           auto_search: 'true',
//           from: 'hot-list',
//           hot_list_id: this.activeHotList.hot_list_id  // NEW: For score lookup
//         }
//       });
//     }
//   } catch (error) {
//     console.error('❌ Error viewing hot list:', error);
//   }
// },
async viewFullList() {
  if (!this.activeHotList || this.activeCandidateIds.length === 0) return;
  
  try {
    // STEP 1: Fetch candidates WITH scores from DB
    const response = await axios.get(
      `/api/v1/hot-lists/${this.activeHotList.hot_list_id}/candidates-with-scores`
    );
    
    const candidatesWithScores = response.data.candidates;
    if (candidatesWithScores.length === 0) {
      alert(`No candidates in "${this.activeHotList.name}" yet.`);
      return;
    }
    console.log('🔥 Fetched hot list scores:', candidatesWithScores.length);
    
    // STEP 2: Build scores map for backend
    const scoresMap = {};
    const metadataMap = {};  // NEW: Track metadata too

    candidatesWithScores.forEach(c => {
      scoresMap[c.id] = c.match_score !== null ? c.match_score : 1.0;
      metadataMap[c.id] = c.metadata;  // NEW: Pass metadata from DB

    });
    
    // STEP 3: ALWAYS use search_set for hot lists (even small ones)
    // This ensures scores are passed to backend consistently
    const searchSetResponse = await axios.post('/api/v1/search-sets/', {
      candidate_ids: candidatesWithScores.map(c => c.id),
      metadata: {
        hot_list_id: this.activeHotList.hot_list_id,
        hot_list_name: this.activeHotList.name,
        scores: scoresMap,  // ← Backend reads from here
        match_metadata: metadataMap  // NEW: {candidate_id: metadata_object}

      }
    });
    
    console.log('✅ Created search_set with scores for', candidatesWithScores.length, 'candidates');
    
    this.$router.push({
      path: '/',
      query: {
        search_set: searchSetResponse.data.search_set_id,  // ← Always use search_set
        auto_search: 'true',
        from: 'hot-list'
      }
    });
    
  } catch (error) {
    console.error('❌ Error viewing hot list:', error);
    // Fallback to old behavior without scores
    if (this.activeCandidateIds.length > 40) {
      this.createSearchSetAndNavigate();
    } else {
      this.$router.push({
        path: '/',
        query: {
          candidate_id: this.activeCandidateIds.join(','),
          auto_search: 'true'
        }
      });
    }
  }
},

    async createSearchSetAndNavigate() {
      try {
        const response = await axios.post('/api/v1/search-sets/', {
          candidate_ids: this.activeCandidateIds,
          metadata: {
            hot_list_id: this.activeHotList.hot_list_id,
            hot_list_name: this.activeHotList.name
          }
        });
        
        this.$router.push({
          path: '/',
          query: {
            search_set: response.data.search_set_id,
            auto_search: 'true',
            from: 'hot-list',
            hot_list_name: this.activeHotList.name
          }
        });
      } catch (error) {
        console.error('❌ Error creating search set:', error);
      }
    },
    
    async refreshHotList(hotListId) {
      try {
        this.refreshing = hotListId;
        // capture current state of the list so we can detect a change
        const current = this.hotLists.find(l => l.hot_list_id === hotListId);
        const originalCount = current ? current.candidate_count : null;
        const originalTs = current ? (current.last_matched_at ? new Date(current.last_matched_at).getTime() : null) : null;

        await axios.post(`/api/v1/hot-lists/${hotListId}/refresh`);
        
        // Poll for seen change in count instead of fixed timeout
        await this.pollForRefreshCompletion(hotListId, { originalCount, originalTs });
        // keep activeHotList in sync if it was selected
        if (this.activeHotList) {
          this.activeHotList = this.hotLists.find(l => l.hot_list_id === this.activeHotList.hot_list_id) || null;
        }

        this.refreshing = null;
      } catch (error) {
        console.error('❌ Error refreshing hot list:', error);
        this.refreshing = null;
      }
  },
  async pollForRefreshCompletion(hotListId, { originalCount = null, originalTs = null } = {}) {
  const maxAttempts = 10;
  const interval = 1500;
  
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise(resolve => setTimeout(resolve, interval));
    
    const response = await axios.get('/api/v1/hot-lists');
    const updatedList = response.data.hot_lists.find(l => l.hot_list_id === hotListId);
    
    if (!updatedList) {
      console.warn(`⚠️ Hot list ${hotListId} disappeared from list`);
      continue;
    }
    
    const updatedCount = updatedList.candidate_count;
    const updatedTs = updatedList.last_matched_at;
    const updatedTsMs = updatedTs ? new Date(updatedTs).getTime() : null;
    
    // SIMPLE LOGIC: Wait for last_matched_at to update
    // (Backend sets this when matching completes)
    if (originalTs && updatedTsMs && updatedTsMs !== originalTs) {
      // Timestamp changed = refresh completed
      this.hotLists = response.data.hot_lists;
      console.log(`✅ Refresh complete: ${updatedCount} candidates`);
      return;
    }
    
    // NEW LIST CASE: No original timestamp, wait for first match
    if (!originalTs && updatedTsMs) {
      this.hotLists = response.data.hot_lists;
      console.log(`✅ Initial match complete: ${updatedCount} candidates`);
      return;
    }
  }
  
  // Timeout fallback - still update UI with latest data
  console.warn(`⚠️ Polling timeout for ${hotListId}, updating anyway`);
  const response = await axios.get('/api/v1/hot-lists');
  this.hotLists = response.data.hot_lists;
},      


    handleHotListCreated(newHotList) {
      this.showCreateModal = false;
      this.loadHotLists();
      
      // Optionally auto-select the new hot list
      setTimeout(() => {
        const created = this.hotLists.find(l => l.hot_list_id === newHotList.hot_list_id);
        if (created) {
          this.selectHotList(created);
        }
      }, 1000);
    },
    
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },
    
    formatTimeAgo(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    }
  },
  watch: {
  async '$route'(to, from) {
    // Only process if we're navigating WITH query params (not clearing them)
    if (!to.query.hot_list_id) return;
    
    const hotListId = to.query.hot_list_id;
    const autoActivate = to.query.auto_activate === 'true';
    
    // Only auto-activate from notifications
    if (hotListId && autoActivate) {
      console.log('🔔 Route watcher: Hot list notification detected:', hotListId);
      
      // Wait for hot lists to load if not loaded yet
      if (this.hotLists.length === 0) {
        await this.loadHotLists();
      }
      
      // Find and activate the hot list
      const list = this.hotLists.find(l => l.hot_list_id === hotListId);
      
      if (list) {
        await this.selectHotList(list);
        console.log(`✅ Auto-activated hot list "${list.name}" from notification`);
        
        // Clean up URL
        this.$router.replace({ 
          path: '/',
          query: {} 
        });
      } else {
        console.error(`❌ Hot list ${hotListId} not found`);
      }
    }
  }
}
};
</script>

<style scoped>
.hot-list-controls {
  position: relative;
}

/* Selector Button */
.selector-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8c42 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
  width:100%;
}

.selector-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
}

.selector-btn .badge {
  background: rgba(255, 255, 255, 0.3);
  padding: 0.17rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    margin: 0.2rem;
}

.selector-btn .fa-chevron-down {
  transition: transform 0.2s ease;
}

.selector-btn .fa-chevron-down.rotated {
  transform: rotate(180deg);
}

/* Dropdown Menu */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 320px;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  color: #6c757d;

  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.create-btn:hover {
  background: #ff5252;
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: #6c757d;
}

.empty-state i {
  font-size: 2.5rem;
    color: #6c757d;

  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.hot-list-items {
  max-height: 400px;
  overflow-y: auto;
}

.hot-list-item {
  display: flex;
  color: #6c757d;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s ease;
}

.hot-list-item:hover {
  background: #f8f9fa;
}

.hot-list-item.active {
  background: #fff3cd;
  border-left: 3px solid #ff6b6b;
}

.list-info {
  flex: 1;
}

.list-name {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.list-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.candidate-count {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.refresh-btn {
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  transition: color 0.2s ease;
}

.refresh-btn:hover {
  color: #ff6b6b;
}

.refresh-btn i.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.clear-selection {
  padding: 1rem;
  text-align: center;
  color: #dc3545;
  font-weight: 500;
  cursor: pointer;
  border-top: 1px solid #e9ecef;
}

.clear-selection:hover {
  background: #fff5f5;
}

/* Banner */
.hot-list-banner {
  background: linear-gradient(135deg, #fff3cd 0%, #ffe4a0 100%);
  border: 1px solid #ffc107;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.banner-content i.fa-filter {
  color: #ff6b6b;
  font-size: 1.25rem;
}

.banner-text {
  flex: 1;
  color: #856404;
}

.banner-text strong {
  color: #ff6b6b;
}

.view-all-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: white;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  color: #856404;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  background: #ffc107;
  color: white;
}

.close-banner {
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: none;
  color: #856404;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.close-banner:hover {
  opacity: 1;
}

/* Transitions */
.dropdown-fade-enter-active, .dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from, .dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}



/* list details */
.hot-list-details {
  margin-top: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #dee2e6;
}

.details-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.details-actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn, .delete-btn, .close-btn {
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.edit-btn {
  background: #007bff;
  color: white;
}

.edit-btn:hover {
  background: #0056b3;
}

.delete-btn {
  background: #dc3545;
  color: white;
}

.delete-btn:hover {
  background: #c82333;
}

.close-btn {
  background: #6c757d;
  color: white;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.detail-card {
  background: white;
  border-radius: 6px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.detail-item {
  margin-bottom: 0.5rem;
  color: #6c757d;

}

.detail-item label {
  font-weight: 500;
  color: #6c757d;
  margin-right: 0.5rem;
}

.skills-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.skill-tag {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.skill-tag.required {
  background: #ffe5e5;
  color: #dc3545;
}

.skill-tag.nice {
  background: #e7f5e7;
  color: #28a745;
}

.description-card {
  background: white;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.description-card p {
  margin: 0.5rem 0 0;
  color: #495057;
  line-height: 1.6;
}

.list-item-content {
  flex: 1;
  cursor: pointer;
}

.list-actions {
  display: flex;
  gap: 0.25rem;
}

.icon-btn {
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s;
}

.icon-btn:hover {
  color: #007bff;
}
.texto-lefty{
  position: absolute; 
  right: 0;
  padding:10px;
}
</style>